/* Location Switcher Popup */

// Cookie management functions
function setCookie(name, value, days) {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = `; expires=${date.toUTCString()}`;
  }
  document.cookie = `${name}=${value || ''}${expires}; path=/; SameSite=Lax`;
}

function getCookie(name) {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === ' ') c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}

// IP-based geolocation service
async function getUserCountry() {
  try {
    // Try multiple services as fallbacks
    const services = [
      'https://ipapi.co/country/',
      'https://ipinfo.io/country',
      'https://api.country.is'
    ];

    for (const service of services) {
      try {
        const response = await fetch(service);
        if (response.ok) {
          let country;
          if (service.includes('country.is')) {
            const data = await response.json();
            country = data.country;
          } else {
            country = await response.text();
          }
          return country.trim().toUpperCase();
        }
      } catch (error) {
        console.log(`Failed to get location from ${service}:`, error);
        continue;
      }
    }
    
    // Fallback: try to detect from timezone
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (timezone.includes('Auckland') || timezone.includes('Wellington')) {
      return 'NZ';
    }
    
    return null;
  } catch (error) {
    console.log('All geolocation services failed:', error);
    return null;
  }
}

// Main Location Switcher Class
if (!customElements.get('location-switcher')) {
  // Wait for modal-dialog to be defined, then extend Modal like other popups
  customElements.whenDefined('modal-dialog').then(() => {
    // Location Switcher Modal - Deliberately overrides standard modal close behaviors
    // Users MUST click action buttons ("Yes, please" or "No, thanks") to dismiss
    // No ESC key, background click, or X button closing allowed
    class LocationSwitcher extends customElements.get('modal-dialog') {
    constructor() {
      super();
      this.cookie = `${this.id}-dismissed`;
      this.targetCountry = this.dataset.targetCountry || 'NZ';
      this.targetUrl = this.dataset.targetUrl;
      this.dismissDays = parseInt(this.dataset.dismissDays) || 7;
      this.showOnMobile = this.dataset.showOnMobile === 'true';
      this.isOpen = false;



      // Don't show in Shopify design mode unless being edited
      if (Shopify.designMode) {
        document.addEventListener('shopify:section:select', (evt) => {
          if (evt.target === this.closest('.shopify-section')) this.open();
        });
        document.addEventListener('shopify:section:deselect', this.close.bind(this));
        return;
      }

      // Early validation - don't proceed if missing required settings
      if (!this.targetUrl || !this.targetCountry) {
        console.log('Location switcher: Missing target URL or country code');
        return;
      }

      // Check if already dismissed
      if (getCookie(this.cookie)) {
        return;
      }

      // Check device compatibility
      if (!this.showOnMobile && window.innerWidth < 768) {
        return;
      }

      // Start location detection
      this.init();
    }



    async init() {
      try {
        const userCountry = await getUserCountry();
        
        // Only show popup if user is from target country AND we have a target URL
        if (userCountry === this.targetCountry && this.targetUrl) {
          setTimeout(() => {
            // Double-check conditions before opening
            if (!getCookie(this.cookie) && !this.isOpen) {
              this.open();
            }
          }, 1000); // Small delay for better UX
        }
      } catch (error) {
        console.log('Location detection failed:', error);
      }
    }

    connectedCallback() {
      // Setup event listeners
      const redirectBtn = this.querySelector('.js-redirect-btn');
      const stayBtn = this.querySelector('.js-close-modal'); // The "No, thanks" button
      
      if (redirectBtn) {
        redirectBtn.addEventListener('click', this.handleRedirect.bind(this));
      }
      
      if (stayBtn) {
        stayBtn.addEventListener('click', this.close.bind(this));
      }

      // The parent modal class adds its own click listener in the constructor
      // We need to override it to prevent background clicks from closing the modal
    }

    // Override the parent's handleClick to prevent background closes
    handleClick(evt) {
      // Parent modal closes on background click (evt.target === this) OR close button
      // We ONLY want to close on our specific close button, not background clicks
      if (evt.target.matches('.js-close-modal')) {
        this.close();
      }
      // Ignore background clicks (evt.target === this) - do not close
      // This prevents accidental dismissal while preserving button functionality
    }

    open() {
      // Prevent multiple opens
      if (this.isOpen || this.hasAttribute('open')) {
        return;
      }

      // Final validation before opening
      if (!this.targetUrl) {
        console.log('Location switcher: Cannot open without target URL');
        return;
      }

      this.isOpen = true;
      
      // Set the open attribute manually to avoid parent's ESC key handler
      this.setAttribute('open', '');
      this.openedBy = null; // No specific opener element
      
      // Focus management - prioritize the redirect button (primary action)
      const redirectBtn = this.querySelector('.js-redirect-btn');
      if (redirectBtn) {
        redirectBtn.focus();
      }
      
      // Pause media (from parent's open method)
      if (window.pauseAllMedia) {
        window.pauseAllMedia();
      }
      
      // DON'T add ESC key handler - we want to prevent ESC closing
      
      // Track opening
      this.dispatchEvent(new CustomEvent('location-switcher:opened'));
    }

    close() {
      // Prevent multiple closes
      if (!this.isOpen && !this.hasAttribute('open')) {
        return;
      }

      this.isOpen = false;
      
      // Use the theme's modal system
      super.close();
      
      // Set dismissal cookie
      setCookie(this.cookie, true, this.dismissDays);
      
      // Track closing
      this.dispatchEvent(new CustomEvent('location-switcher:closed'));
    }

    // Cleanup method for safety
    disconnectedCallback() {
      // Call parent cleanup
      if (super.disconnectedCallback) {
        super.disconnectedCallback();
      }
      
      // Ensure our state is clean
      this.isOpen = false;
    }

    handleRedirect() {
      if (this.targetUrl) {
        // Track redirect
        this.dispatchEvent(new CustomEvent('location-switcher:redirected', {
          detail: { targetUrl: this.targetUrl }
        }));
        
        // Redirect to target URL
        window.location.href = this.targetUrl;
      }
    }
  }

  customElements.define('location-switcher', LocationSwitcher);
  });
}

// Analytics integration (if Google Analytics is available)
if (typeof gtag !== 'undefined') {
  document.addEventListener('location-switcher:opened', () => {
    gtag('event', 'popup_shown', {
      event_category: 'Location Switcher',
      event_label: 'Popup Displayed'
    });
  });

  document.addEventListener('location-switcher:redirected', (e) => {
    gtag('event', 'redirect_clicked', {
      event_category: 'Location Switcher',
      event_label: e.detail.targetUrl
    });
  });

  document.addEventListener('location-switcher:closed', () => {
    gtag('event', 'popup_dismissed', {
      event_category: 'Location Switcher',
      event_label: 'Popup Closed'
    });
  });
}